import { logger } from "@services/workers/helpers";
import { CARE_PLAN_DISCOUNT_ID, CARE_PLAN_DISCOUNT_PERCENTAGE, VALID_CARE_PLANS_DISCOUNTS, VALID_JOB_TYPES } from "@src/constants";
import { state, ui } from "../../../../../../../../interfaces/interfaces";
import { dispatchSetMultipleState } from './../../../../../../../../reducers/stateReducer/index';
import { RightioWBSAPIModelsSPSpDiscountsGetDiscountsResult, RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult } from "@src/utilities/apis/wbs/model";

const name = "DiscountsTab";

/**
 * Calculates the rate within the given constraints.
 * @param {number} rate - The rate to be adjusted.
 * @param {RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult} constrains - The constraints for the rate.
 * @returns {[number, boolean]} - The adjusted rate and a boolean indicating if a limit was hit.
 */
export const getRate = (rate: number, constrains: RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult): [number, boolean] => {
    const lowerLimit = Number(constrains.maximumReducedRate);
    const upperLimit = (constrains.rate && Number(constrains.rate - 1)) || 0;
    if (rate > upperLimit) {
        return [upperLimit, true];
    }
    if (rate < lowerLimit) {
        return [lowerLimit, true];
    }
    return [Math.floor(rate), false];
};

/**
 * Handles the change event for the rate discount input.
 * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - The change event.
 * @param {RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult | undefined} charge - The charge details.
 */
export const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, charge: RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult | undefined): void => {
    if (!charge) return;
    const value = e.target.value;
    const [rate, limitHit] = getRate(Math.floor(Number(value)), charge);
    if (limitHit) return;
    state.ChargeRateAdj = Number(rate);
};

/**
 * Handles the change event for the rate reduction reason.
 * @param {React.SyntheticEvent<Element, Event> | null} event - The change event.
 * @param {RightioWBSAPIModelsSPSpDiscountsGetDiscountsResult | null} value - The selected discount value.
 */
export const handleChangesType = (event: React.SyntheticEvent<Element, Event> | null, value: RightioWBSAPIModelsSPSpDiscountsGetDiscountsResult | null): void => {
    logger.debug(name + "->" + handleChangesType.name, "Rate Reduction Reason: ", value);
    if (value?.discountId && value.discountId > 0) {
        dispatchSetMultipleState({
            ChargeReductionDiscount: value.discountId === CARE_PLAN_DISCOUNT_ID ? CARE_PLAN_DISCOUNT_PERCENTAGE : value.discount,
            ChargeReductionDiscountDetails: value.discountId === CARE_PLAN_DISCOUNT_ID ? `${value.discountDescription} (${value.isPercentage ? "%" : "£"}${(CARE_PLAN_DISCOUNT_PERCENTAGE).toFixed(2)})` : `${value.discountDescription} (${value.isPercentage ? "%" : "£"}${value.discount?.toFixed(2)})`,
            ChargeRateReductionDiscountId: value.discountId,
            ChargeReductionIsPercentage: value.isPercentage,
            CallReducedRate: 1
        });
    } else {
        dispatchSetMultipleState({
            ChargeReductionDiscount: 0.0,
            ChargeReductionDiscountDetails: "",
            ChargeRateReductionDiscountId: 0,
            ChargeReductionIsPercentage: false,
            CallReducedRate: 0
        });
    }
    ui.RatesRateReason = value || undefined;
};

/**
 * Determines if the job type is a gas boiler service job eligible for a CC Combo Gas Boiler Service Care Plan discount.
 * @param {string} jobTypeCode - The job type code.
 * @returns {boolean} - True if the job type is a gas boiler service job, false otherwise.
 */
export const isGasBoilerServiceJob = (jobTypeCode: string) => VALID_JOB_TYPES.includes(jobTypeCode);

/**
 * Determines applicable care plans discounts.
 * @param {string} jobTypeCode - The job type code.
 * @returns {number[]} - The applicable care plans discounts.
 */
export const validCarePlansDiscounts = (jobTypeCode: string): number[] => isGasBoilerServiceJob(jobTypeCode)
    ? VALID_CARE_PLANS_DISCOUNTS
    : VALID_CARE_PLANS_DISCOUNTS.filter(x => x === CARE_PLAN_DISCOUNT_ID);